/**
 * What kind of classical evening this is, as a tag the feed can show and filter.
 *
 * Doug: "the classical feed includes a lot of educational stuff etc. that I
 * don't want; don't strip it from the database but I want the feed to be
 * performances. Like with the subtitles for theatre, let's add simple tags like
 * chamber, orchestral, choral, and make these filterable too."
 *
 * So this is the same shape as the surtitle gate: a deterministic label on the
 * row, drawn on the card, offered as a filter, and used to hide one category
 * outright. Nothing is deleted — `educational` rows are still in `entries` and
 * still in the `feed` view, they are simply not drawn.
 *
 * The vocabulary is `src/classical.ts`'s, deliberately. The scorer already
 * decides chamber-versus-orchestral on these words and having the card say one
 * thing while the funnel believes another is how a feed stops making sense.
 * The difference is what each is for: the scorer excludes, this describes.
 *
 * Read from the room first. Berlin's halls separate orchestral from chamber
 * architecturally and the room is a fact about the building rather than a guess
 * about the evening — but only where the words have not already said otherwise,
 * because a choir in the Großer Saal is still a choir.
 */
import type { FeedRow } from './types'

export type ClassicalForm =
  | 'orchestral' | 'chamber' | 'choral' | 'recital' | 'opera' | 'educational'

export const FORM_LABEL: Record<ClassicalForm, string> = {
  orchestral: 'Orchestral',
  chamber: 'Chamber',
  choral: 'Choral',
  recital: 'Recital',
  opera: 'Opera',
  educational: 'Talk or workshop',
}

/**
 * Not a performance: a lecture, an introduction, a workshop, a guided tour, a
 * reading, something for schools.
 *
 * This is the list Doug's complaint is actually about. The Konzerthaus files
 * "Reading in German Language" and "Konzerteinführung" as concerts, and they
 * arrive in the Orchestral lane next to Mahler.
 */
const EDUCATIONAL = [
  /\beinführung\b|\bkonzerteinführung\b|\bwerkeinführung\b/i,
  /\bvortrag\b|\blecture\b|\bmasterclass\b|\bmeisterkurs\b/i,
  /\bworkshop\b|\bmitmach|\bfamilienkonzert\b|\bkinderkonzert\b/i,
  /\bschulkonzert\b|\bfür schulen\b|\bschool concert\b/i,
  /\bführung\b(?!.*\bentführung\b)|\bguided tour\b/i,
  /\blesung\b|\breading in\b/i,
  /\bprobe\b|\böffentliche generalprobe\b|\brehearsal\b/i,
  /\bgespräch\b|\bpodium\b|\bdiskussion\b|\btalk\b/i,
  /\b2 x hören\b/i,
]

const CHORAL = [
  /\bchor\b|\bchoir\b|\bchoral\b|\bkammerchor\b|\brundfunkchor\b/i,
  /\boratorium\b|\boratorio\b|\brequiem\b|\bmesse\b|\bmass\b|\bpassion\b|\bvesper/i,
  /\bkantate\b|\bcantata\b|\bte deum\b|\bmagnificat\b/i,
]

const CHAMBER = [
  /\bstreichquartett\b|\bquartett\b|\bquartet\b/i,
  /\btrio\b|\bquintett\b|\bquintet\b|\bduo\b|\bsextett\b|\boktett\b/i,
  /\bkammermusik\b|\bchamber music\b|\bkammerorchester\b/i,
]

const RECITAL = [
  /\bliederabend\b|\bklavierabend\b|\bsolorezital\b|\bsolo recital\b/i,
  /\borgelkonzert\b|\borgan matinee\b|\borgelmatinee\b/i,
  /\brezital\b|\brecital\b/i,
]

const ORCHESTRAL = [
  /\bsinfonie\b|\bsymphon|\bsinfonisch/i,
  /\bkonzert für\b|\bconcerto\b|\bklavierkonzert\b|\bviolinkonzert\b/i,
  /\bphilharmoniker\b|\bstaatskapelle\b|\borchester\b|\borchestra\b/i,
  /\bsuite\b|\bouvertüre\b|\boverture\b|\btone poem\b/i,
]

const hit = (res: RegExp[], text: string) => res.some(re => re.test(text))

/** Fold a name for comparison: drop a parenthetical role like "(Piano)", the
 *  diacritics and the case, so "Víkingur Ólafsson" and "vikingur olafsson"
 *  match and a soloist can be recognised in the composer column. */
const fold = (name: string) =>
  name.replace(/\(.*?\)/g, '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase()

/** Null for anything that is not a classical concert or an opera. */
export function classicalForm(e: FeedRow): ClassicalForm | null {
  if (e.discipline !== 'classical' && e.discipline !== 'opera') return null
  if (e.type !== 'concert' && e.type !== 'performance') return null

  const p = e.performers ?? {}
  const text = [e.title, p.programme ?? '', p.ensemble ?? '', e.venue ?? ''].join(' ')

  // First, because a workshop in a concert hall is still a workshop and the
  // room would otherwise call it orchestral.
  if (hit(EDUCATIONAL, text)) return 'educational'

  if (e.discipline === 'opera' || e.type === 'performance') return 'opera'

  // Words before rooms: a choir in the Großer Saal is a choir, and the room
  // only knows what the hall is shaped for.
  if (hit(CHORAL, text)) return 'choral'
  if (hit(CHAMBER, text)) return 'chamber'
  if (hit(RECITAL, text)) return 'recital'
  if (hit(ORCHESTRAL, text)) return 'orchestral'

  // Then the building, which is right about 99% of rows and is the only thing
  // left when a title says nothing — "Guest performance" being 115 of them.
  if (e.room_scale === 'chamber') return 'chamber'
  if (e.room_scale === 'recital') return 'recital'
  if (e.room_scale === 'orchestral') return 'orchestral'

  return null
}

/**
 * The title a classical card should carry.
 *
 * Doug: "the titles for the classical events should be the main piece being
 * performed and the composer". Where both are known that is exactly what this
 * returns — "Händel · Belshazzar". Where only the programme is known it is the
 * work alone, and where neither is it returns null and the card keeps the
 * house's own title.
 *
 * 455 of 633 concerts carry a programme and 158 a composer, so the middle case
 * is the common one and the composer is a bonus rather than the rule.
 *
 * The house title still wins when it says something the music does not — see
 * `rowMeta.ts`'s `rowTitle`, which makes that call for every row and for the
 * sheet (WP 2.4; it was `CardBody`'s Programme shape before the nine shapes
 * became one row).
 */
export function classicalTitle(e: FeedRow): { work: string; composer: string | null } | null {
  // Phase 5b: an opera performance's own composer and work, canonical where
  // `stage.ts` matched one — read before the classical path below, which is
  // null for every performance row (`entries.classical` is concerts only; an
  // opera performance is decided the way a concert is, which is why it reaches
  // this function at all — `cardShape.ts` said so until WP 2.4 deleted it).
  if (e.type === 'performance' && e.stage && (e.stage.composer || e.stage.work)) {
    return { work: e.stage.work ?? e.title, composer: e.stage.composer ?? null }
  }

  // Phase 4b: where the normaliser has already worked out which item is the
  // evening's main piece, use its answer — canonical name and title where the
  // reference matched, the source's own where it did not — rather than
  // re-guessing from the same strings by index and a regex over length.
  const main = e.classical?.programme.find(i => i.is_main)
  if (main) return { work: main.work ?? main.work_src, composer: main.composer ?? main.composer_src }

  const p = e.performers ?? {}
  const works = (p.programme ?? '').split(/\s*;\s*/).map(w => w.trim()).filter(Boolean)

  // The houses list a concerto's soloist in the same column as its composer —
  // "Beethoven; Uchida; Bruckner", where Uchida is the pianist — so the title
  // could name the performer as if she wrote the piece. Doug: "I ONLY want the
  // composer for orchestral works." A name that is also a soloist, the
  // conductor or the ensemble is a performer, never the composer.
  const performers = new Set(
    [p.soloists, p.conductor, p.ensemble]
      .filter(Boolean)
      .flatMap(s => s!.split(/\s*[;,]\s*/))
      .map(fold)
      .filter(Boolean),
  )
  const isComposer = (name: string) => !performers.has(fold(name))
  const composerList = (raw: string | undefined) =>
    (raw ?? '').split(/\s*;\s*/).map(c => c.trim()).filter(Boolean).filter(isComposer)

  // An opera, and the works overview the houses publish for one: a composer
  // and no programme, because the title already *is* the work. "Salome" and
  // "La Vestale" want their composer and nothing else, and 92 of the 158
  // rows with a composer are this shape.
  if (!works.length) {
    const only = composerList(p.composer)
    return only.length === 1 ? { work: e.title, composer: only[0]! } : null
  }

  // The same anchor rule the card already uses, so the title and the works
  // line cannot disagree about which piece is the main one.
  const MAJOR = /sinfoni|symphon|konzert für|concerto|requiem|messe|mass\b|oper\b|oratori|passion/i
  let work = works[0]
  let best = -1
  for (const w of works) {
    const rank = (MAJOR.test(w) ? 1000 : 0) + w.length
    if (rank > best) { work = w; best = rank }
  }

  // Composers arrive as "Brahms; Schumann" in programme order, so the one that
  // goes with the anchor work is the one at the same index. Where the counts
  // do not line up, no composer is claimed rather than the wrong one. Soloists
  // are dropped first, which is also what re-aligns a concerto's list: "Beethoven;
  // Uchida; Bruckner" against two works becomes "Beethoven; Bruckner".
  const composers = composerList(p.composer)
  const at = works.indexOf(work)
  const composer =
    composers.length === works.length && at >= 0 ? composers[at]
    : composers.length === 1 ? composers[0]
    : null

  // The composer, taken back out of the work.
  //
  // The Philharmoniker's programme runs the two together — "Olivier Messiaen
  // Vingt Regards sur l'Enfant-Jésus" — so a card that prints the composer
  // in front of it says the name twice: "Messiaen · Olivier Messiaen Vingt
  // Regards". Every full name we know about is removed from the front of
  // the work before it is used.
  const cleaned = composer ? stripComposer(work, composer) : work

  return { work: cleaned || work, composer: composer ?? null }
}

/** The title to show where there is room for one line, not the card's several.
 *
 *  A concert's own house title is often the performer list — "Berliner
 *  Philharmoniker, Gustavo Dudamel, Mitsuko Uchida" — which is the bloat Doug
 *  flagged on the rail. Where the music is known this returns "Composer · Work"
 *  the same way the full card's headline does; otherwise the house title
 *  stands, because a title that says something the music does not is not bloat.
 */
export function conciseTitle(e: FeedRow): string {
  const named = classicalTitle(e)
  if (!named) return e.title
  return named.composer ? `${surname(named.composer)} · ${named.work}` : named.work
}

/** Remove a composer's own name from the front of the work it wrote. Anchored
 *  at the start and on a word boundary: taking "Bach" out wherever it appears
 *  would leave "-Busoni" behind. */
function stripComposer(work: string, composer: string): string {
  const escaped = composer.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return work.replace(new RegExp(`^\\s*${escaped}\\b[\\s:,–—-]*`, 'i'), '').trim()
}

/** A surname is what a concert listing uses. "Georg Friedrich Händel" is the
 *  database being complete; "Händel" is what fits on a card.
 *
 *  The last word, which is what a programme prints. I briefly made this
 *  keep the particle — "van Beethoven", "de Falla" — on the theory that a
 *  particle belongs to the name it precedes. Checked against the live data
 *  it was worse: a concert programme says **Beethoven**, and "van Beethoven"
 *  appeared on the card as soon as the Philharmoniker's composers landed.
 *  The particle convention differs by language and by composer and there is
 *  no rule; the last word is right far more often than not.
 *
 *  The exceptions are hand-kept, because a double surname has no tell. */
const DOUBLE = /^(vaughan williams|lloyd webber|saint-saëns|saint-saens|dall'abaco|mendelssohn bartholdy)$/i

export const surname = (name: string) => {
  const clean = name.replace(/\(.*?\)/g, '').trim()
  // "Rimsky-Korsakow, Nikolai" — a few sources invert.
  if (clean.includes(',')) return clean.split(',')[0]!.trim()

  const parts = clean.split(/\s+/)
  if (parts.length < 2) return clean

  const lastTwo = parts.slice(-2).join(' ')
  if (DOUBLE.test(lastTwo)) return lastTwo

  return parts[parts.length - 1]!
}
